import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "../lib/cn";
import { controlSize } from "../lib/variants";
import { Spinner } from "./spinner";

/** Figma «Button»: Type = primary | secondary | outline; State = default | hover | focus | disable. */
export const buttonVariants = cva(
  "relative inline-flex max-w-full shrink-0 select-none items-center justify-center whitespace-nowrap rounded-control border border-transparent font-semibold " +
    "transition-[background-color,color,border-color,box-shadow,transform] outline-none focus-visible:shadow-[var(--shadow-focus)] active:scale-[0.98] " +
    "disabled:pointer-events-none disabled:border-transparent disabled:bg-disabled disabled:text-disabled-fg [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-fg hover:bg-primary-hover",
        secondary: "bg-primary-soft text-primary hover:bg-primary-soft-hover",
        outline: "border-primary bg-surface text-primary hover:bg-primary-soft",
        ghost: "text-fg hover:bg-surface-2",
        danger: "bg-danger text-white hover:bg-danger/90",
        success: "bg-success text-white hover:bg-success/90",
        dark: "bg-fg text-surface hover:bg-fg/85",
        link: "h-auto! border-0 px-0! text-primary underline-offset-4 hover:underline active:scale-100 disabled:bg-transparent",
      },
      size: { sm: "", md: "", lg: "", xl: "" },
      fullWidth: { true: "w-full" },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    ref?: Ref<HTMLButtonElement>;
    loading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    /** Рендер дочернего элемента (например, <Link>) со стилями кнопки. */
    asChild?: boolean;
  };

export function Button({
  className, variant, size = "md", fullWidth, loading, leftIcon, rightIcon, asChild, disabled, children, type = "button", ...props
}: ButtonProps) {
  const classes = cn(controlSize({ size }), buttonVariants({ variant, size, fullWidth }), className);
  if (asChild) return <Slot.Root className={classes} {...props}>{children}</Slot.Root>;
  return (
    <button type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {loading ? <Spinner /> : leftIcon}
      {/* Строка длиннее экрана — многоточие внутри кнопки, а не вылет за край */}
      {typeof children === "string" ? <span className="min-w-0 overflow-hidden text-ellipsis">{children}</span> : children}
      {!loading && rightIcon}
    </button>
  );
}

const iconSizes = { sm: "size-9", md: "size-12", lg: "size-14", xl: "size-16" } as const;

export type IconButtonProps = Omit<ButtonProps, "leftIcon" | "rightIcon" | "fullWidth" | "children"> & {
  icon: ReactNode;
  /** Обязателен: читается скринридером и показывается как title. */
  "aria-label": string;
};

export function IconButton({ icon, size = "md", variant = "ghost", className, loading, ...props }: IconButtonProps) {
  // При загрузке иконка заменяется спиннером, а не стоит рядом с ним
  return (
    <Button variant={variant} size={size} title={props["aria-label"]} className={cn("px-0", iconSizes[size ?? "md"], className)} loading={loading} {...props}>
      {!loading && icon}
    </Button>
  );
}
